import React, { useState } from 'react';
import styled, { css } from "styled-components";
import { useDebounce } from "react-use";

interface SearchProps {
    borderColor?: string;
    placeholder?: string;
    onChange?: (text: string) => void;
    debounceFinish?: (text: string) => void;
};

const Wrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;

    svg {
        position: absolute;
        left: 13px;
        width: 14px;
        height: 14px;
        pointer-events: none;
        color: var(--cds-text-muted);
        transition: color 0.25s ease;
        flex-shrink: 0;
    }

    &:focus-within svg {
        color: var(--cds-accent);
    }
`;

type SearchStyledProps = { $borderColor?: string };

const Search = styled.input<SearchStyledProps>`
    width: 100%;
    background: var(--cds-surface-input);
    border: 1px solid var(--cds-border-subtle);
    border-radius: 6px;
    padding: 9px 16px 9px 38px;
    font-size: 0.8rem;
    color: var(--cds-text-primary);
    outline: none;
    line-height: 1.5;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;

    &::placeholder {
        color: var(--cds-text-muted);
        font-style: italic;
        letter-spacing: 0.02em;
    }

    &:hover {
        border-color: rgba(var(--cds-accent-rgb), 0.3);
        background: var(--cds-surface-input-hover);
    }

    &:focus {
        border-color: transparent;
        background: var(--cds-surface-input-focus);
        box-shadow:
            0 0 0 1.5px var(--cds-accent),
            0 0 16px rgba(var(--cds-accent-rgb), 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.03);
    }

    ${ (props: SearchStyledProps) => props.$borderColor && props.$borderColor !== 'transparent' && css`
        border-color: ${props.$borderColor};
    `}
`;

const SearchDebounce: React.FC<SearchProps> = (props: SearchProps) => {

    const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
    const [q, setQ] = useState<string>('');

    const [, ] = useDebounce(() => {

        if(isFirstTime) {
            setIsFirstTime(false);
        }
        else {
            props.debounceFinish?.(q);
        }

    }, 500, [q]);

    return(
        <Wrapper>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <Search
                type="text"
                placeholder={props.placeholder}
                $borderColor={props.borderColor}
                value={q}
                onChange={(e: any) => {
                    let search: string = e.target.value;
                    setQ(search);
                    props.onChange?.(search);
                }}
            />
        </Wrapper>
    );

};

export default React.memo(SearchDebounce);
